import React, { Component, FormEvent, ReactNode } from 'react';
import Editor from '@monaco-editor/react';
import { RootState } from '../../../redux/store';
import { changeIsBasicUpdate } from '../../../redux/actions/BasicActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { FormicaProps, withFormica } from '../../../helpers/withFormica';
import { query, repository, subscribe } from '@trlogic/client-js';
import CollectionNames from '../../../constant/CollectionNames';
import { requireNonUndefined } from '../../../util/Assert';
import { InstanceContainer } from '../../../config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import { CommonConnectionModel } from '../../../domain/connection/ConnectionDomains';
import { changeQuery, clearQuery } from '../../../redux/actions/QueryActions';
import DataTypeSelectBoxComponent from '../../custom/DataTypeSelectBoxComponent';
import FormicaLabel from '../../custom/FormicaLabel';
import FormicaSearchSelectBox, { FormicaSelectBoxItem } from '../../custom/FormicaSearchSelectBox';
import { QueryState } from '../../../redux/reducers/QueryReducer';
import DataType from 'constant/DataType';
import { TransactionJsonModel } from '../../../domain/stream/transaction/TransactionDomains';
import CustomLoading from '../../custom/CustomLoading';
import RoleHelper from '../../../RoleHelper';
import FormicaInput from '../../custom/FormicaInput';
import LanguageUtil from '../../../util/LanguageUtil';
import ComponentName from '../../../constant/ComponentName';

class QueryEditorComponent extends Component<Props, State> {
  public state = new State();
  private connectionSubscription: subscribe.Subscription | undefined;
  private transactionSubscription: subscribe.Subscription | undefined;
  private structSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;
  private repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);

  public render(): ReactNode {
    const {
      query: { queryVariable },
    } = this.props;
    const { loading } = this.state;
    if (loading) {
      return <CustomLoading />;
    }
    return (
      <div className={'grid grid-cols-5 w-full h-full'}>
        <div className={'col-span-1 flex flex-col border-r border-f-black-5 px-5 py-5 gap-y-4'}>
          <div className={'flex flex-col w-full gap-y-1'}>
            <FormicaLabel label={LanguageUtil.get({ id: 'common.label.channel', defaultMessage: 'Channel' })} />
            <FormicaSearchSelectBox
              options={this.getTransactionOptions()}
              rounded
              placeholder={'Please select a'}
              disabled={!RoleHelper.canEdit('EnrichmentQuery')}
              value={queryVariable.transaction}
              onChange={(e: FormicaSelectBoxItem) => {
                this.onChangeTransaction(e);
              }}
              data-testid={`${ComponentName.QUERY_VARIABLE}_channel_select`}
            />
          </div>
          <div className={'flex flex-col w-full gap-y-1'}>
            <FormicaLabel label={LanguageUtil.get({ id: 'common.label.connection', defaultMessage: 'Connection' })} />
            <FormicaSearchSelectBox
              options={this.getConnectionOptions()}
              rounded
              placeholder={'Please select a'}
              disabled={!RoleHelper.canEdit('EnrichmentQuery')}
              value={queryVariable.connection}
              onChange={(e: FormicaSelectBoxItem) => {
                this.onChangeConnection(e);
              }}
              data-testid={`${ComponentName.QUERY_VARIABLE}_connection_select`}
            />
          </div>
          <div className={'flex flex-col w-full gap-y-1'}>
            <FormicaLabel label={LanguageUtil.get({ id: 'common.label.dataType', defaultMessage: 'Data Type' })} />
            <DataTypeSelectBoxComponent
              properties={queryVariable.properties}
              disabled={!RoleHelper.canEdit('EnrichmentQuery')}
              onChangeValue={this.onDataTypeChange}
              data-testid={`${ComponentName.QUERY_VARIABLE}_datatype_select`}
            />
          </div>
          <div className={'flex flex-col w-full gap-y-1'}>
            <FormicaLabel label={LanguageUtil.get({ id: 'common.label.timeoutSeconds', defaultMessage: 'Timeout (seconds)' })} />
            <FormicaInput
              value={queryVariable.timeout}
              type='number'
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.timeout', defaultMessage: 'Timeout(ms)' })}
              disabled={!RoleHelper.canEdit('EnrichmentQuery')}
              onChange={(e: FormEvent<HTMLInputElement>) => this.onChangeTimeout(e)}
              data-testid={`${ComponentName.QUERY_VARIABLE}_timeout_input`}
            />
          </div>
        </div>
        <div className={'col-span-4 flex flex-col w-full h-full px-5 py-5 gap-y-2'}>
          <FormicaLabel label={'Value'} />
          <Editor
            options={{
              readOnly: !RoleHelper.canEdit('EnrichmentQuery'),
            }}
            value={queryVariable.query}
            onChange={(e: string | undefined) => this.onQueryChange(e as string)}
            defaultLanguage={'sql'}
            data-testid={`${ComponentName.QUERY_VARIABLE}_query_input`}
          />
        </div>
      </div>
    );
  }

  public async componentDidMount() {
    await this.startTracker();
  }

  public async componentWillUnmount() {
    await this.stopTracker();
  }

  private startTracker = async () => {
    const connectionQuery = query.QueryBuilder.select().from(CollectionNames.CONNECTIONS);
    const transactionQuery = query.QueryBuilder.select().from(CollectionNames.TRANSACTIONS);
    this.transactionSubscription = subscribe.Formica.subscribe(CollectionNames.TRANSACTIONS, transactionQuery.build());
    this.connectionSubscription = subscribe.Formica.subscribe(CollectionNames.CONNECTIONS, connectionQuery.build());
    this.structSubscription = subscribe.Formica.subscribe(
      CollectionNames.DATA_STRUCTURES,
      query.QueryBuilder.select().from(CollectionNames.DATA_STRUCTURES).build(),
    );
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.connectionSubscription, this.transactionSubscription, this.structSubscription]);
  };

  private tracker = async () => {
    if (this.connectionSubscription?.ready() && this.transactionSubscription?.ready() && this.structSubscription?.ready()) {
      const connectionQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.CONNECTIONS).build();
      const transactionQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.TRANSACTIONS).build();
      const connections: CommonConnectionModel[] = await this.repository.findMany(connectionQuery);
      const transactions: TransactionJsonModel[] = await this.repository.findMany(transactionQuery);
      this.setState({ connections, transactions, loading: false });
    }
  };

  private stopTracker = async () => {
    await this.transactionSubscription?.stop();
    await this.connectionSubscription?.stop();
    await this.structSubscription?.stop();
    this.computation?.stop();
  };

  private onQueryChange = (query: string) => {
    const {
      query: { queryVariable },
      changeQuery,
    } = this.props;
    if (!RoleHelper.canEdit('EnrichmentQuery')) return;
    queryVariable.query = query;
    changeQuery(queryVariable);
  };

  private onDataTypeChange = (e: FormicaSelectBoxItem) => {
    const {
      query: { queryVariable },
      changeQuery,
    } = this.props;
    queryVariable.properties = e.properties || { dataType: DataType.STRING };
    changeQuery(queryVariable);
  };

  private getConnectionOptions() {
    return this.state.connections.map((connection) => {
      return {
        label: connection.name,
        value: connection.name,
      };
    });
  }

  private getTransactionOptions() {
    return this.state.transactions.map((transaction) => {
      return {
        label: transaction.name,
        value: transaction.name,
      };
    });
  }

  private onChangeTransaction(e: FormicaSelectBoxItem) {
    const {
      query: { queryVariable },
      changeQuery,
    } = this.props;
    queryVariable.transaction = e.value;
    changeQuery(queryVariable);
  }

  private onChangeConnection(e: FormicaSelectBoxItem) {
    const {
      query: { queryVariable },
      changeQuery,
    } = this.props;
    queryVariable.connection = e.value;
    changeQuery(queryVariable);
  }

  private onChangeTimeout(e: React.FormEvent<HTMLInputElement>) {
    const {
      query: { queryVariable },
      changeQuery,
    } = this.props;
    queryVariable.timeout = parseInt(e.currentTarget.value);
    changeQuery(queryVariable);
  }
}

interface Props extends ReduxProps, FormicaProps {}

class State {
  connections: CommonConnectionModel[] = [];
  transactions: TransactionJsonModel[] = [];
  loading: boolean = true;
}

const mapStateToProps = (state: RootState) => ({
  query: state.query as QueryState,
  isUpdate: state.query.isUpdate,
  lastCategory: state.page.lastCategory,
});

const mapDispatchToProps = { changeQuery, changeIsBasicUpdate, clearQuery };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;
export default withFormica(connector(QueryEditorComponent));
